import * as R from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { Dropdown, Segment, FormField } from 'semantic-ui-react'
import { iconText } from '../QuestionConditionModal'
import {
  getQuestionChoiceDropdownOptions,
  getQuestionLabel,
  getSafeLabel,
} from '../QuestionConditionalLogic/utils'
import { LinkButton } from 'elements/LinkButton'

export default function QuesitonVisibility({
  questionList,
  question,
  visibilityCondition,
  handleUpdateVisibility,
  handleDeleteVisibility,
  questionIdWithVisibilityRuleError,
  isLogicButtonDisabled = false,
}) {
  const [visibility, setVisibility] = useState(visibilityCondition)
  const [questionOptions, setQuestionOptions] = useState([])
  const [choiceOptions, setChoiceOptions] = useState([])

  const currnetQuestionId = question.id || question.uniqueIdentifier

  const getChoiceOptionFromQuestionId = useCallback((questions, questionId) => {
    const selectedQuestion = questions.find((item) => item.id === questionId)
    const choices = getQuestionChoiceDropdownOptions(selectedQuestion)

    return choices
  }, [])

  useEffect(() => {
    setVisibility(visibilityCondition)
  }, [visibilityCondition])

  useEffect(() => {
    const currentQuestionIndex = questionList.findIndex(
      (item) => item.id == currnetQuestionId || item.uniqueIdentifier == currnetQuestionId
    )
    const sourceLimitIndex = currentQuestionIndex === -1 ? 0 : currentQuestionIndex

    // filter question and format as dropdown option value
    const questionsOptionFormat = questionList
      .filter(
        (item, index) =>
          item.id != currnetQuestionId &&
          index < sourceLimitIndex &&
          item.type != 'comment' &&
          item.type != 'file_upload'
      )
      .map(({ id, text, type, choices, is_opinion_scale }) => ({
        id: id,
        key: id,
        text: getQuestionLabel(type, text),
        search_text: text,
        value: id,
        choices: choices,
        type: type,
        is_opinion_scale: is_opinion_scale,
      }))

    setQuestionOptions(questionsOptionFormat)

    // if the question have visibility rule will prepare choice option into dropdown
    if (visibility?.sourceQuestionId) {
      const choiceData = getChoiceOptionFromQuestionId(
        questionsOptionFormat,
        visibility.sourceQuestionId
      )
      setChoiceOptions(choiceData)
    }
  }, [questionList, currnetQuestionId, getChoiceOptionFromQuestionId, visibility])

  const addVisibility = () => {
    const newVisibility = {
      id: 'temp_visibility_id',
      conditionalQuestionId: currnetQuestionId,
      sourceQuestionId: 'temp_id_souce_question',
      rule: [],
    }
    setVisibility(newVisibility)
    handleUpdateVisibility(newVisibility)
  }

  const deleteVisibility = () => {
    setChoiceOptions([])
    setVisibility({})
    handleUpdateVisibility({})
    if (visibility.id !== 'temp_visibility_id') {
      handleDeleteVisibility(visibility)
    }
  }

  function handleChangeSourceQuestion(questionId) {
    setVisibility({
      ...visibility,
      sourceQuestionId: questionId,
      rule: [],
    })

    const formatChoices = getChoiceOptionFromQuestionId(questionOptions, questionId)
    setChoiceOptions(formatChoices)

    handleUpdateVisibility({
      ...visibility,
      sourceQuestionId: questionId,
      rule: [],
    })
  }

  function handleChangeRule(rule) {
    setVisibility({
      ...visibility,
      rule,
    })

    handleUpdateVisibility({
      ...visibility,
      rule,
    })
  }

  function getLabel(option) {
    const selectedVisibilityQuestion = questionList?.find(
      (item) => item.id === visibility.sourceQuestionId
    )
    const shouldShowChoiceValueOnRule =
      (selectedVisibilityQuestion?.type === 'slider' ||
        selectedVisibilityQuestion?.type === 'star') &&
      option.choice?.value

    const label = getSafeLabel(option.choice)

    if (R.includes(option.value, ['positive', 'neutral', 'negative'])) {
      return {
        content: <span className={`bold text-${iconText[option.value]}`}>{option.text}</span>,
        color: sentimentLabelColor[option.sentiment],
      }
    }

    const sentiment = option.choice?.sentiment
    const isStarType = selectedVisibilityQuestion?.type === 'star'
    if (isStarType && sentiment) {
      return {
        content: (
          <span className={`bold text-${iconText[sentiment]}`}>
            {`${option.choice?.value} Star${option.choice?.value == 1 ? '' : 's'}`}
          </span>
        ),
        color: sentimentLabelColor[sentiment],
      }
    }
    if (sentiment) {
      return {
        content: (
          <span>
            {shouldShowChoiceValueOnRule ? (
              <>
                <i className={`text-${iconText[sentiment]} icon ${iconText[sentiment]}`} />
                <span className={`bold text-${iconText[sentiment]}`}>{option.choice.value}</span>
              </>
            ) : (
              <span className={`bold text-${iconText[sentiment]}`}>{label}</span>
            )}
          </span>
        ),
        color: sentimentLabelColor[sentiment],
      }
    }

    return {
      content: <span className="bold">{label}</span>,
    }
  }

  function getErrorVisibilityRule() {
    const existingQuestionId = R.prop('id', question)
    if (existingQuestionId && existingQuestionId === questionIdWithVisibilityRuleError) {
      return true
    }

    const tempQuestionId = R.prop('uniqueIdentifier', question)
    if (tempQuestionId && tempQuestionId === questionIdWithVisibilityRuleError) {
      return true
    }

    return false
  }

  //refactor
  const filterRuleOptions = useCallback((options, currentRules) => {
    if (!options || !currentRules || currentRules.length === 0) {
      return options
    }

    // normalise all selected values to lowercase strings
    const selectedValues = new Set(currentRules.map((v) => String(v).toLowerCase()))

    // Build a map: sentiment → array of choice value strings
    const sentimentToChoices = {}
    for (const option of options) {
      const sentiment = option.choice?.sentiment?.toLowerCase()
      if (sentiment) {
        const value = String(option.value).toLowerCase()
        if (!sentimentToChoices[sentiment]) {
          sentimentToChoices[sentiment] = []
        }
        sentimentToChoices[sentiment].push(value)
      }
    }

    return options.filter((option) => {
      const value = String(option.value).toLowerCase()

      // Always keep selected items visible
      if (selectedValues.has(value)) {
        return true
      }

      // Hide choice options whose sentiment header is selected
      const choiceSentiment = option.choice?.sentiment?.toLowerCase()
      if (choiceSentiment && selectedValues.has(choiceSentiment)) {
        return false
      }

      // Hide sentiment headers if all their child choices are selected
      const sentiment = option.sentiment?.toLowerCase()
      if (sentiment) {
        const relatedChoiceValues = sentimentToChoices[sentiment] || []
        if (relatedChoiceValues.length === 0) {
          return false
        }
        const allChoicesSelected = relatedChoiceValues.every((v) => selectedValues.has(v))
        return !allChoicesSelected // hide if all related selected
      }

      return true
    })
  }, [])

  if (visibility && visibility?.sourceQuestionId) {
    return (
      <>
        <p>Show this question only if the following condition is met:</p>
        <Segment>
          <FormField>
            <label>
              If the answer to question
              <i
                className="float-right icon fas fa-xmark text-grey link"
                onClick={deleteVisibility}
              />
            </label>
            <Dropdown
              data-testid="visibility-source-question"
              value={visibility.sourceQuestionId}
              placeholder="Select question"
              onChange={(e, { value }) => handleChangeSourceQuestion(value)}
              selection
              options={questionOptions}
              search={(options, query) =>
                options.filter((opt) => opt.search_text.toLowerCase().includes(query.toLowerCase()))
              }
              noResultsMessage="No more available questions"
              fluid
            />
          </FormField>
          <FormField>
            <label>Includes</label>
            <Dropdown
              data-testid="visibility-source-choice"
              value={visibility.rule.map((item) => String(item))}
              placeholder="Select choice"
              onChange={(e, { value }) => handleChangeRule(value)}
              selection
              multiple
              options={filterRuleOptions(choiceOptions, visibility.rule).map((option) => ({
                ...option,
                value: String(option.value),
              }))}
              search
              noResultsMessage="No more available choices"
              fluid
              renderLabel={getLabel}
              error={getErrorVisibilityRule()}
            />
          </FormField>
        </Segment>
      </>
    )
  }

  return (
    <LinkButton
      type="button"
      onClick={addVisibility}
      disabled={isLogicButtonDisabled}
      data-testid="add-visibility-rule-btn"
    >
      <i className="icon far fa-plus" />
      Add visibility rule
    </LinkButton>
  )
}

// refactor
const sentimentLabelColor = {
  positive: '!bg-green-lighter',
  neutral: '!bg-orange-lighter',
  negative: '!bg-red-lighter',
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/QuesitonVisibility/index.js